import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import {
  EDIT_PROJECT,
  GET_CHECK_PROJECT_APPROVALS,
  GET_SEARCH_MEMBERS
} from '../../gql';
import { validationSchema } from './validations/edit-project-validation-schema';
import { errorToast, successToast } from '../../../common/utils/alert';
import { errorMessages } from '../../../common/utils/constants';
import { IPermissions } from '../../../common/utils/interfaces';
import NumberInput from '../../../common/widgets/number-input';
import TextFieldComponent from '../../../common/widgets/text-field';
import DatePickerComponent from '../../../common/widgets/date-picker';
import AutoCompleteSearchComponent from '../../../common/widgets/auto-complete-search';
import { useAppSelector } from '../../../../redux/hooks';
import ApprovalText from '../../../common/widgets/approval-text';
import { format } from 'date-fns';
import { ApprovalRequest, IEditProjectValues, IProjectData } from '../../type';
import { projectTypes } from './create-project-modal';
import ProjectAllocation from '../project-allocation';
import { isSameDate } from '../../../common/utils/helperFunctions';
import { UserRoleAppFormat } from '../../../common/utils/enum';

interface IEditProject {
  project: IProjectData;
  closeRequestForm: () => void;
  editProjectCallback: () => void;
  permissions: IPermissions;
  isEditModalVisible: boolean;
}

const EditProjectModal = ({
  project,
  permissions,
  isEditModalVisible,
  closeRequestForm,
  editProjectCallback
}: IEditProject): JSX.Element => {
  const deliveryResponsibleMember = project.members.find((member) =>
    member?.memberRoles.some(
      (role) => role.projectRole.role === 'DELIVERY_RESPONSIBLE'
    )
  );

  const hoursPerWeek = deliveryResponsibleMember?.hoursPerWeek ?? 0;
  const memberID = deliveryResponsibleMember?.member?.id ?? '';
  const memberName = deliveryResponsibleMember?.member?.displayName ?? '';

  const { profileData } = useAppSelector((state) => state.profileData);

  const [projectManager, setProjectManager] =
    useState<Record<string, string | number>>();

  const [actionUserId, setActionUserId] = useState<string>('');
  const [loadingModal, setloadingModal] = useState(false);
  const [initialFormState, setInitialFormState] = useState<IEditProjectValues>({
    id: project.id,
    projectName: project.projectName,
    clientName: project.clientName,
    tentativeStartDate: new Date(project.tentativeStartDate).toISOString(),
    tentativeEndDate: new Date(project.tentativeEndDate).toISOString(),
    projectType: project.projectType,
    hypercarePeriod: project.hypercarePeriod,
    projectMember: memberID,
    hoursPerWeekForPMOrDR: hoursPerWeek,
    isMaximumAllocation: false
  });
  const [projectNameValue, setProjectNameValue] = useState(
    initialFormState.projectName
  );
  const [clientNameValue, setClientNameValue] = useState(
    initialFormState.clientName
  );
  const [searchingProjectManagerId, setSearchingProjectManagerId] =
    useState<string>(memberID);
  const [
    hoursPerWeekForPMOrDRAllocationValue,
    sethoursPerWeekForPMOrDRAllocationValue
  ] = useState<string>(initialFormState.hoursPerWeekForPMOrDR.toString());
  const [approvalRequestsProjects, setApprovalRequestsProjects] =
    useState<ApprovalRequest>();

  const [editProjectMutation] = useMutation(EDIT_PROJECT);

  const {
    refetch: statusApprovalRequestsRefetch,
    data: statusApprovalRequestsData
  } = useQuery(GET_CHECK_PROJECT_APPROVALS, {
    variables: { projectId: project.id }
  });

  const defaultHypercarePeriod = project.hypercarePeriod;
  const defaultTentativeEndDate = new Date(project.tentativeEndDate);

  const handleSubmit = (values: IEditProjectValues): void => {
    closeRequestForm();
    editProjectMutation({
      variables: {
        actionUserId,
        id: project.id,
        projectName: values.projectName,
        clientName: values.clientName,
        tentativeStartDate: new Date(values.tentativeStartDate).toISOString(),
        tentativeEndDate: new Date(values.tentativeEndDate).toISOString(),
        projectType: values.projectType,
        hypercarePeriod: values.hypercarePeriod,
        projectManagerId: values.projectMember,
        hoursPerWeekForPMOrDR: values.hoursPerWeekForPMOrDR
      }
    })
      .then(async () => {
        if (defaultHypercarePeriod !== values.hypercarePeriod) {
          successToast('Project details updated successfully');
        } else if (
          !isSameDate(
            defaultTentativeEndDate,
            new Date(values.tentativeEndDate)
          )
        ) {
          if (profileData?.roles.includes(UserRoleAppFormat.OperationsLead)) {
            successToast('Project details updated successfully');
            await statusApprovalRequestsRefetch();
          } else {
            successToast('Request sent for approval');
            await statusApprovalRequestsRefetch();
          }
        } else {
          successToast('Project details updated successfully');
        }
        editProjectCallback();
        setInitialFormState(values);
      })
      .catch((error) => {
        console.error(error);
        errorToast(errorMessages.ErrormessageEditingProject);
      });
  };

  const handleCancel = async (): Promise<void> => {
    closeRequestForm();
    await statusApprovalRequestsRefetch();
  };

  useEffect(() => {
    if (statusApprovalRequestsData) {
      setApprovalRequestsProjects(statusApprovalRequestsData);
    }
  }, [statusApprovalRequestsData]);

  useEffect(() => {
    if (profileData) {
      if (profileData.memberId) {
        setActionUserId(profileData.memberId);
      }
    }
  }, [profileData]);

  useEffect(() => {
    if (memberID && memberName) {
      setProjectManager({ id: memberID, displayName: memberName });
      setloadingModal(true);
    }
  }, [deliveryResponsibleMember]);

  useEffect(() => {
    setInitialFormState((prevState) => ({
      ...prevState,
      projectMember: memberID,
      hoursPerWeekForPMOrDR: hoursPerWeek
    }));
  }, [memberID]);

  let pendingRequests = false;
  if (approvalRequestsProjects?.checkApprovalRequest) {
    const passedData = JSON.parse(
      approvalRequestsProjects.checkApprovalRequest
    );
    if (
      passedData.projectId === project.id &&
      passedData.status !== 'APPROVED' &&
      passedData.status !== 'REJECTED'
    ) {
      pendingRequests = true;
    }
  }
  return (
    <>
      {loadingModal && (
        <Modal
          open={Boolean(isEditModalVisible)}
          onClose={closeRequestForm}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{
            marginTop: '25px',
            overflow: 'scroll'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '24px',
              p: '35px'
            }}
          >
            <Formik
              onSubmit={handleSubmit}
              initialValues={initialFormState}
              validationSchema={validationSchema}
            >
              {({ setFieldValue, errors, touched, values }) => (
                <Form className={'form'} data-testid="form">
                  <Box
                    sx={{
                      maxWidth: '678px',
                      padding: '5px 5px 5px 5px',
                      borderRadius: '28px',
                      boxShadow: 'none',
                      background:
                        'linear-gradient(0deg, rgba(41, 93, 171, 0.11), rgba(41, 93, 171, 0.11)), #FDFBFF'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        onClick={() => {
                          void (async () => {
                            await handleCancel();
                          })();
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        padding: '0px 20px'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '24px',
                          fontWeight: 400,
                          color: '#1A1B1F'
                        }}
                      >
                        Edit Project
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <TextFieldComponent
                          label={'Project Name'}
                          value={projectNameValue}
                          onChange={(value) => {
                            setProjectNameValue(value);
                            void setFieldValue('projectName', value);
                          }}
                          touched={touched.projectName}
                          errors={errors.projectName}
                        />
                        <TextFieldComponent
                          label={'ClientName(Optional)'}
                          value={clientNameValue}
                          onChange={(value) => {
                            setClientNameValue(value);
                            void setFieldValue('clientName', value);
                          }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <Typography sx={{ marginTop: '15px' }}>
                            Project Type
                          </Typography>
                          <Select
                            labelId="project-type-select-label"
                            value={values.projectType}
                            onChange={(event: SelectChangeEvent) => {
                              void setFieldValue(
                                'projectType',
                                event.target.value
                              );
                            }}
                            sx={{ marginTop: '10px' }}
                          >
                            {projectTypes.map((tab) => (
                              <MenuItem key={tab.label} value={tab.label}>
                                {tab.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.projectType && errors.projectType && (
                            <Typography
                              sx={{
                                color: 'red',
                                fontSize: '10px'
                              }}
                            >
                              Project type is mandatory.
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            gap: '15px'
                          }}
                        >
                          <Box
                            sx={{
                              flexGrow: 1
                            }}
                          >
                            <DatePickerComponent
                              label={'Tentative Start Date'}
                              format={'MM/dd/yyyy'}
                              value={
                                new Date(initialFormState.tentativeStartDate)
                              }
                              onChange={function (event: Date | null): void {
                                if (event) {
                                  void setFieldValue(
                                    'tentativeStartDate',
                                    format(event, 'MM/dd/yyyy')
                                  );
                                }
                              }}
                              touched={touched.tentativeStartDate}
                              errors={errors.tentativeStartDate}
                              disabled={false}
                            />
                          </Box>

                          <Box
                            sx={{
                              flexGrow: 1
                            }}
                          >
                            <DatePickerComponent
                              label={'Tentative End Date'}
                              value={
                                new Date(initialFormState.tentativeEndDate)
                              }
                              format={'MM/dd/yyyy'}
                              onChange={function (event: Date | null): void {
                                if (event) {
                                  void setFieldValue(
                                    'tentativeEndDate',
                                    format(event, 'MM/dd/yyyy')
                                  );
                                }
                              }}
                              touched={touched.tentativeEndDate}
                              errors={errors.tentativeEndDate}
                              disabled={pendingRequests}
                            />
                            <Box
                              sx={{
                                display: 'flex',
                                marginTop: '5px'
                              }}
                            >
                              {pendingRequests && (
                                <ApprovalText
                                  color="#D1DCFB"
                                  label="Waiting for Approval"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>

                        <Box>
                          <NumberInput
                            label={'Hypercare Period'}
                            defaultValue={initialFormState.hypercarePeriod}
                            onChange={(value) => {
                              void setFieldValue('hypercarePeriod', value);
                            }}
                            min={0}
                            max={999}
                            text={'Weeks'}
                          />
                          <Typography sx={{ color: 'red', fontSize: '0.7rem' }}>
                            {touched.hypercarePeriod && errors.hypercarePeriod}
                          </Typography>
                        </Box>
                        <AutoCompleteSearchComponent
                          label={'Project Manager/Delivery Responsible'}
                          query={{
                            query: GET_SEARCH_MEMBERS,
                            getter: 'getMembers'
                          }}
                          optionLabelKey={'displayName'}
                          value={projectManager}
                          onChange={(newValue) => {
                            void setFieldValue('projectMember', newValue?.id);
                            if (newValue) {
                              setSearchingProjectManagerId(
                                newValue.id.toString()
                              );
                              void setFieldValue('projectMember', newValue.id);
                            } else {
                              setSearchingProjectManagerId('');
                            }
                          }}
                          touched={touched.projectMember}
                          errors={errors.projectMember}
                          disabled={
                            !(
                              project.createdById === profileData?.memberId ||
                              profileData?.permissions?.project_manger?.update
                            )
                          }
                        />

                        <Box sx={{ mt: '20px' }}>
                          {searchingProjectManagerId && (
                            <ProjectAllocation
                              memberId={searchingProjectManagerId}
                              projectId={project.id}
                              isAdd={true}
                              value={hoursPerWeekForPMOrDRAllocationValue}
                              onChange={(value) => {
                                const newValue = parseInt(value, 10);
                                sethoursPerWeekForPMOrDRAllocationValue(value);
                                void setFieldValue(
                                  'hoursPerWeekForPMOrDR',
                                  newValue
                                );
                              }}
                              touched={touched.hoursPerWeekForPMOrDR}
                              errors={errors.hoursPerWeekForPMOrDR}
                              setIsMaximumAllocation={(value) => {
                                void setFieldValue(
                                  'isMaximumAllocation',
                                  value
                                );
                              }}
                              disabled={
                                !(
                                  project.createdById ===
                                    profileData?.memberId ||
                                  profileData?.permissions?.project_manager
                                    ?.update
                                )
                              }
                            />
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row-reverse',
                            gap: '8px',
                            marginTop: '30px'
                          }}
                        >
                          <LoadingButton
                            type="submit"
                            sx={{
                              fontWeight: 500,
                              letterSpacing: '0.1px',
                              fontSize: '18px',
                              textTransform: 'capitalize'
                            }}
                          >
                            Confirm
                          </LoadingButton>
                          <Button
                            onClick={() => {
                              void (async () => {
                                await handleCancel();
                              })();
                            }}
                            sx={{
                              fontWeight: 500,
                              letterSpacing: '0.1px',
                              fontSize: '18px',
                              textTransform: 'capitalize'
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Modal>
      )}
    </>
  );
};
export default EditProjectModal;
